import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, shareReplay, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { User } from '../shared/interfaces/user.interface';
import { ddnSessionToken } from '../utils/ddn-session';
import { isHomeContent } from '../content/content-home.util';

export type PreferredEditor = 'wysiwyg' | 'tiptap' | 'modern-web' | 'jodit' | 'editorjs';

export interface EditorJsData {
  time?: number;
  blocks?: Array<{ id?: string; type: string; data: Record<string, unknown> }>;
  version?: string;
}

export interface Content {
  _id?: string;
  title: string;
  content: string;
  created?: Date;
  updated?: Date | string;
  status?: 'published' | 'unpublished' | 'archived';
  pageTitle?: string;
  routeSlug?: string;
  preferredEditor?: PreferredEditor;
  editorJsData?: EditorJsData | null;
}

/** Lean projection from GET info-title/:title?meta=1 */
export type ContentMeta = Pick<Content, '_id' | 'title' | 'status' | 'updated' | 'pageTitle'>;

export type ContentChangeKind = 'save' | 'status' | 'delete';

export interface ContentChangeEvent {
  id: string;
  title: string;
  updated?: string | Date;
  status?: Content['status'];
  routeSlug?: string;
  kind: ContentChangeKind;
}

const CONTENT_BROADCAST_CHANNEL = 'webddn-content-updated';

@Injectable({
  providedIn: 'root'
})
export class ContentService implements OnDestroy {
  private user: User | undefined;
  private infoRoutesCache$: Observable<Content[]> | null = null;
  private readonly contentChanged$ = new Subject<ContentChangeEvent>();
  private readonly broadcastChannel: BroadcastChannel | null =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CONTENT_BROADCAST_CHANNEL) : null;
  /** Latest Home `updated` from CMS mutations or a successful live sync (survives route changes). */
  private homeLiveUpdatedAt: string | null = null;

  /** Emits when CMS content is saved, status changes, or deleted (same tab + cross-tab). */
  readonly contentChanges$ = this.contentChanged$.asObservable();

  getHomeLiveUpdatedAt(): string | null {
    return this.homeLiveUpdatedAt;
  }

  setHomeLiveUpdatedAt(updated: string | null): void {
    this.homeLiveUpdatedAt = updated;
  }

  constructor(private http: HttpClient, private cookieService: CookieService) {
    this.broadcastChannel?.addEventListener('message', (ev: MessageEvent<ContentChangeEvent>) => {
      if (ev.data?.id && ev.data?.kind) {
        this.emitContentChange(ev.data, false);
      }
    });
  }

  ngOnDestroy(): void {
    this.broadcastChannel?.close();
    this.contentChanged$.complete();
  }

  private getAuthHeaders(): HttpHeaders {
    const rawSession = this.cookieService.get('ddnsession');
    const token = ddnSessionToken(rawSession);
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  getAllContent(page?: number, limit?: number, afterId?: string | null, search?: string): Observable<any> {
    let url = `${environment.BASEURL}${environment.INFO_APIURL}`;
    const params = new URLSearchParams();
    if (page) params.set('page', page.toString());
    if (limit) params.set('limit', limit.toString());
    if (afterId) params.set('after_id', afterId);
    if (search) params.set('search', search);
    
    const queryString = params.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
    return this.http.get<any>(url);
  }

  getAllInfoRoutes(): Observable<Content[]> {
    if (!this.infoRoutesCache$) {
      this.infoRoutesCache$ = this.http
        .get<Content[]>(`${environment.BASEURL}${environment.INFO_ROUTE_APIURL}`)
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    }
    return this.infoRoutesCache$;
  }

  clearInfoRoutesCache(): void {
    this.infoRoutesCache$ = null;
  }

  /** Call after CMS mutations so public pages and header nav can refresh live over SSG. */
  afterContentMutation(event: ContentChangeEvent): void {
    if (this.affectsInfoRoutes(event)) {
      this.clearInfoRoutesCache();
    }
    this.emitContentChange(event, true);
  }

  mutationFromContent(content: Partial<Content> & { title: string }, kind: ContentChangeKind, id: string): void {
    this.afterContentMutation({
      id,
      title: content.title,
      updated: content.updated,
      status: content.status,
      routeSlug: content.routeSlug,
      kind,
    });
  }

  static affectsInfoRoutes(event: ContentChangeEvent): boolean {
    return (
      event.kind === 'delete' ||
      event.kind === 'status' ||
      event.routeSlug !== undefined ||
      event.kind === 'save'
    );
  }

  private affectsInfoRoutes(event: ContentChangeEvent): boolean {
    return ContentService.affectsInfoRoutes(event);
  }

  private emitContentChange(event: ContentChangeEvent, broadcast: boolean): void {
    if (isHomeContent(event.title) && event.updated != null) {
      this.homeLiveUpdatedAt = String(event.updated);
    }
    this.contentChanged$.next(event);
    if (broadcast) {
      try {
        this.broadcastChannel?.postMessage(event);
      } catch {
        // Channel closed or unavailable
      }
    }
  }

  getContentById(id: string): Observable<Content> {
    return this.http.get<Content>(`${environment.BASEURL}${environment.INFO_APIURL}/${id}`);
  }

  getInfoByTitle(title: string, options?: { meta?: boolean }): Observable<Content> {
    const base = `${environment.BASEURL}${environment.INFO_TITLE_APIURL}/${title}`;
    const url = options?.meta ? `${base}?meta=1` : base;
    return this.http.get<Content>(url);
  }

  getInfoMetaByTitle(title: string): Observable<ContentMeta> {
    return this.http.get<ContentMeta>(
      `${environment.BASEURL}${environment.INFO_TITLE_APIURL}/${title}?meta=1`
    );
  }

  createContent(content: Content): Observable<Content> {
    const headers = this.getAuthHeaders();
    return this.http.post<Content>(`${environment.BASEURL}${environment.INFO_APIURL}`, content, { headers });
  }

  updateContent(id: string, update: Partial<Content>): Observable<Content> {
    const headers = this.getAuthHeaders();
    return this.http.put<Content>(`${environment.BASEURL}${environment.INFO_APIURL}/${id}`, update, { headers });
  }

  updateContentStatus(id: string, status: 'published' | 'unpublished' | 'archived'): Observable<Content> {
    const headers = this.getAuthHeaders();
    return this.http.put<Content>(`${environment.BASEURL}${environment.INFO_APIURL}/${id}`, { status }, { headers });
  }

  deleteContent(id: string): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.delete(`${environment.BASEURL}${environment.INFO_APIURL}/${id}`, { headers });
  }
}
